import { Observable } from "rxjs";
import NetworkService from "../network/networkService";
import { AgTechQueryParam, OdQueryParam } from "../enums";
import { helperFunctions } from "../helper";
import { IInventoryAllResponse } from "../models/IInventory.model";
import { IProductAllResponse } from "../models/IProduct.model";
import { URLConfig } from "../network/urlStore";

export const getInventory = (
  queryParams?: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }>
): Observable<IInventoryAllResponse> => {
  let url = URLConfig.getInventoryTrialMasterURL();
  for (const param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IInventoryAllResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching Inventory data", error);
      }
    );
  });
};

export const getInventoryNextPage = (
  nextPageURI: string
): Observable<IInventoryAllResponse> => {
  const url = URLConfig.getInventoryNextPageURL(nextPageURI);
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res) => {
        if (res) {
          const response = res as unknown as IInventoryAllResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: any) => {
        subscriber.error(error);
        console.log("Error while fetching Inventory next page ", error);
      }
    );
  });
};

export const getInventoryProduct = (
  queryParams?: Array<{
    key: OdQueryParam;
    value: string[];
  }>,
  crop?: string,
  season?: string
): Observable<IProductAllResponse> => {
  let url = URLConfig.getInventoryProductTrialMasterURL(crop, season);
  for (const param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IProductAllResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching inventory products", error);
      }
    );
  });
};

export const getInventoryProductNextPage = (
  nextPageURI: string
): Observable<IProductAllResponse> => {
  const url = URLConfig.getInventoryNextPageURL(nextPageURI);
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res) => {
        if (res) {
          const response = res as unknown as IProductAllResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: any) => {
        subscriber.error(error);
        console.log(
          "Error while fetching Inventory products next page ",
          error
        );
      }
    );
  });
};

export const addInventoryProduct = (requestPayload: any): Observable<any> => {
  const url = URLConfig.addInventoryProductsTrialMasterURL();
  return new Observable((subscriber) => {
    NetworkService.post(url, requestPayload).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IProductAllResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while adding product in inventory", error);
      }
    );
  });
};

export const addVariant = (
  requestPayload: any,
  inventoryId: string
): Observable<any> => {
  const url = URLConfig.addVariantTrialMasterURL(inventoryId);
  return new Observable((subscriber) => {
    NetworkService.put(url, requestPayload).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IProductAllResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while adding variants in inventory", error);
      }
    );
  });
};

export const updateVariant = (
  requestPayload: any,
): Observable<any> => {
  const url = URLConfig.updateInventoryTrialMasterURL();
  return new Observable((subscriber) => {
    NetworkService.patch(url, requestPayload).then(
      (res: unknown) => {
        subscriber.next(res);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while updating variant in inventory", error);
      }
    );
  });
};

export const updateInventory = (requestPayload: any): Observable<any> => {
  const url = URLConfig.updateInventoryTrialMasterURL();
  return new Observable((subscriber) => {
    NetworkService.patch(url, requestPayload).then(
      (res: unknown) => {
        subscriber.next(res);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while updating the inventory", error);
      }
    );
  });
};

export const deleteInventory = (inventoryId: string): Observable<any> => {
  const url = URLConfig.deleteInventoryTrialMasterURL(inventoryId);
  return new Observable((subscriber) => {
    NetworkService.delete(url).then(
      (res: unknown) => {
        subscriber.next(res);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while deleting the inventory", error);
      }
    );
  });
};

export const getContentLengthForInventory = (
  queryParams?: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }>
): Observable<number> => {
  let url = URLConfig.getContentLengthForInventoryURL();
  for (const param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }
  return new Observable((subscriber) => {
    NetworkService.head(url).then(
      (response: unknown) => {
        if (response) {
          const { contentLength } = response as { contentLength: number };
          subscriber.next(contentLength);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while deleting the inventory", error);
      }
    );
  });
};

export const getAllInventory = (
  queryParams?: Array<{ key: AgTechQueryParam | OdQueryParam; value: string[] }>
): Observable<any[]> => {
  let allInventories: any[] = [];

  return new Observable((subscriber) => {
    const errorHandler = (error: any) => {
      subscriber.error(error);
    };

    const responseHandler = (response: IInventoryAllResponse) => {
      allInventories = [...allInventories, ...(response.inventories || [])];

      if (response.nextPageUri) {
        getInventoryNextPage(response.nextPageUri).subscribe({
          next: responseHandler,
          error: errorHandler,
        });
      } else {
        subscriber.next(allInventories);
        subscriber.complete();
      }
    };

    getInventory(queryParams).subscribe({
      next: responseHandler,
      error: errorHandler,
    });
  });
};
